/*
 * The Jelly UI design tokens: one small palette expressed as CSS custom
 * properties, shared by the DOM and the canvas painter. Two semantic themes
 * (LIGHT / DARK) group colors by role (background / foreground / border); the
 * flattened *_TOKENS maps and PALETTE expose them to JS, and the FOCUS_RING /
 * SHADOW_* constants ride on the same tokens.
 */

// The three document-wide theme modes ('auto' follows the operating system)
export type ThemeMode = 'light' | 'dark' | 'auto';

// A resolved theme, grouped the way the CSS custom properties are named
export interface Theme {
  background: {
    default: string;
    surface: string;
    muted: string;
    neutral: string;
    neutralEmphasis: string;
    white: string;
    rose: string;
    amber: string;
    azure: string;
    mint: string;
    accent: string;
  };
  foreground: {
    default: string;
    muted: string;
    onEmphasis: string;
    onNeutral: string;
    onWhite: string;
    onAccent: string;
  };
  border: {
    default: string;
    focus: string;
  };
  shadow: string;
}

// A flattened token map: `<group>-<role>` var suffix -> oklch value
export type TokenMap = Record<string, string>;

// The rounded display stack used by every component label
export const FONT_STACK = `ui-rounded, 'SF Pro Rounded', system-ui, -apple-system, 'Segoe UI', sans-serif`;

/*
 * The body/field text stack (inputs, dialogs, cards) and the monospace stack
 * (kbd). Shared here so no component re-types them - override site-wide with
 * the --jelly-font-text / --jelly-font-mono tokens.
 */
export const BODY_FONT_STACK = `system-ui, -apple-system, 'Segoe UI', sans-serif`;
export const MONO_FONT_STACK = `ui-monospace, 'SF Mono', Menlo, monospace`;

/*
 * ---------------------------------------------------------------------------
 * Shared swatches
 * ---------------------------------------------------------------------------
 * The four brand hues and pure white read identically in both modes, so they
 * live here and are referenced from each theme. Every value is oklch; the
 * source hex is kept in a comment. `sky` is the lightened azure dark mode uses
 * for the accent and focus ring, where the base azure is too dim on a dark
 * surface.
 */

const WHITE = 'oklch(1 0 0)';                     // #FFFFFF
const INK   = 'oklch(0.2609 0.0238 267.11)';      // #1F2430

const ROSE  = 'oklch(0.5771 0.2152 27.33)';       // #DC2626
const AMBER = 'oklch(0.5779 0.1495 51.54)';       // #BC5A00
const AZURE = 'oklch(0.5601 0.1577 249.8)';       // #0077CC
const MINT  = 'oklch(0.5489 0.1374 151.7)';       // #178746
const SKY   = 'oklch(0.7112 0.1559 250.96)';      // #4DA6FF

/*
 * ---------------------------------------------------------------------------
 * Semantic themes
 * ---------------------------------------------------------------------------
 * background : page, surfaces, neutral fills, and the saturated hue fills
 * foreground : text tones and the labels placed on top of a fill
 * border     : hairlines and the focus ring
 *
 * The hues carry only their saturated fill plus the label that sits on it -
 * the soft tints and per-hue text tones were unused and have been dropped.
 * `accent` is the primary interactive fill (azure by default, re-tintable per
 * subtree by <jelly-theme accent="...">).
 */

export const LIGHT: Theme = {
  background: {
    default:         WHITE,                          // page background
    surface:         WHITE,                          // raised surface (cards, sheets)
    muted:           'oklch(0.9906 0.0017 247.84)',  // #FBFCFD  input / field rest fill
    neutral:         'oklch(0.8884 0 0)',            // #DADADA  neutral fill (platinum)
    neutralEmphasis: 'oklch(0.4676 0 0)',            // #5A5A5A  strong neutral fill (graphite)
    white:           WHITE,                          // explicit white fill
    rose:            ROSE,
    amber:           AMBER,
    azure:           AZURE,
    mint:            MINT,
    accent:          AZURE,
  },
  foreground: {
    default:    INK,                                 // primary text
    muted:      'oklch(0.503 0.0269 266.68)',        // #5D6474  secondary text
    onEmphasis: WHITE,                               // label on a saturated / graphite fill
    onNeutral:  INK,                                 // label on a neutral fill
    onWhite:    INK,                                 // label on the white fill
    onAccent:   WHITE,                               // label on the accent fill
  },
  border: {
    default: 'oklch(0.9439 0.004 286.32)',           // #ECECEF  hairline
    focus:   AZURE,                                  // focus ring
  },
  shadow: '20, 26, 38',                              // rgb triple, consumed inside rgba()
};

export const DARK: Theme = {
  background: {
    default:         'oklch(0.2198 0.006 236.84)',   // #181B1D
    surface:         'oklch(0.2514 0.0163 264.22)',  // #1E222A
    muted:           'oklch(0.288 0.018 262.21)',    // #262B34
    neutral:         'oklch(0.3674 0.0201 266.01)',  // #3A3F4A  neutral fill + hairline
    neutralEmphasis: 'oklch(0.4707 0.0189 266.12)',  // #565B66
    white:           'oklch(0.94 0.0071 268.55)',    // #E9EBF0
    rose:            ROSE,
    amber:           AMBER,
    azure:           AZURE,
    mint:            MINT,
    accent:          SKY,
  },
  foreground: {
    default:    'oklch(0.9645 0.0054 274.97)',       // #F2F3F7
    muted:      'oklch(0.7595 0.0259 265.54)',       // #A9B1C2
    onEmphasis: WHITE,
    onNeutral:  'oklch(0.9645 0.0054 274.97)',       // #F2F3F7  light label on a dark neutral fill
    onWhite:    INK,                                 // dark label on the near-white fill
    onAccent:   'oklch(0.2514 0.0163 264.22)',       // #1E222A  card surface, not pure black
  },
  border: {
    default: 'oklch(0.3674 0.0201 266.01)',          // #3A3F4A  shares the neutral step
    focus:   SKY,
  },
  shadow: '0, 0, 0',
};

/*
 * ---------------------------------------------------------------------------
 * CSS custom-property maps
 * ---------------------------------------------------------------------------
 * Flatten a semantic theme to { <var-suffix>: value } for --jelly-color-*,
 * naming each token `<group>-<role>` in kebab-case (background-neutral-emphasis,
 * foreground-on-accent, …). This is the only naming layer - there are no
 * aliases to keep in sync.
 */

const kebab = (key: string): string => key.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());

function flatten (t: Theme): TokenMap {
  const out: TokenMap = {};

  for (const group of ['background', 'foreground', 'border'] as const) {
    for (const [role, value] of Object.entries(t[group])) {
      out[`${group}-${kebab(role)}`] = value;
    }
  }

  out['shadow'] = t.shadow;

  return out;
}

// Full var-suffix -> value maps, one per mode
export const LIGHT_TOKENS: TokenMap = flatten(LIGHT);
export const DARK_TOKENS: TokenMap  = flatten(DARK);

/*
 * Convenience lookup for JS code that needs a concrete color (canvas fills,
 * documented defaults). Keyed by the same suffixes as the CSS tokens, so a
 * component's fallback reads `var(--jelly-color-background-neutral,
 * ${PALETTE['background-neutral']})`. Always prefer the CSS tokens in styles.
 */
export const PALETTE: TokenMap = LIGHT_TOKENS;

// Focus ring geometry (and default color) shared by every painted ring. The
// CSS counterparts (--jelly-ring-*, --jelly-shadow-*) are emitted from these
// values by themeTokenCSS() in runtime.ts.
export const FOCUS_RING = {
  color: LIGHT.border.focus,
  width: 4.5,
  gap:   0,
  alpha: 0.5,
};
